import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import { EmptyState, LoadingState } from "../components/common";
import { PortalAuthProvider, usePortalAuth } from "./PortalAuthProvider";
import { PortalLayout } from "./PortalLayout";
import { PortalLeadDetailPage } from "./PortalLeadDetailPage";
import { PortalLeadsPage } from "./PortalLeadsPage";
import { PortalLoginPage } from "./PortalLoginPage";
import { PortalOverviewPage } from "./PortalOverviewPage";

/**
 * The client portal, mounted under `/portal` alongside the console.
 *
 * One bundle, two surfaces. They share an origin because the session cookies are
 * `SameSite=Strict` and a second origin would mean CORS plus a relaxed cookie
 * policy — a weaker auth setup than the one being tested. They share nothing else:
 * separate provider, separate cookie, separate API prefix, and no component that
 * renders both a console and a portal identity.
 *
 * Route paths here are relative to `/portal`, so this subtree can be moved by
 * changing one line in `Root`.
 */
export function PortalApp() {
  return (
    <PortalAuthProvider>
      <Routes>
        <Route path="login" element={<PortalLoginPage />} />
        <Route
          element={
            <PortalProtected>
              <PortalLayout />
            </PortalProtected>
          }
        >
          <Route index element={<PortalOverviewPage />} />
          <Route path="leads" element={<PortalLeadsPage />} />
          <Route path="leads/:id" element={<PortalLeadDetailPage />} />
          <Route
            path="*"
            element={<EmptyState title="Not found" hint="That page does not exist in this portal." />}
          />
        </Route>
      </Routes>
    </PortalAuthProvider>
  );
}

/**
 * Route guard. Convenience, not security: every `/api/portal/*` endpoint resolves
 * the session and its client scope again on the server, so a bypassed guard buys
 * an empty shell rather than someone else's leads.
 */
function PortalProtected({ children }: { children: ReactNode }) {
  const { user } = usePortalAuth();
  const location = useLocation();

  if (user === undefined) {
    return (
      <Box sx={{ p: 4 }}>
        <LoadingState label="Restoring session…" />
      </Box>
    );
  }
  if (user === null) {
    return <Navigate to="/portal/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
